import { EntityType, EntityTypeEnum, TradingEntityMeta, WorkflowActions } from 'api'
import { Locale, Translation } from 'locale'
import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { useDispatch } from 'react-redux'
import { tradingEntityMetasGetterSelector } from 'selectors/tradingEntitySelectors'
import { createWorkflowReferenceDataEntitySelector } from 'selectors/workflowSelectors'
import { GlobalState } from 'store'
import { EntityTypeIdRecord } from 'store/notification/notificationTypes'
import { NEW_ITEM_PREFIX, useParametricSelector } from 'utils'
import { DialogField, DialogRow } from 'view/components/inputs'
import { Groupbox } from 'view/components/panels'
import { DialogWindow } from 'view/components/panels/DialogWindow'
import { PrimeBrokerSelectboxField } from 'view/shared/entity'
import { OwnerSelectboxField } from 'view/shared/entity/OwnerTradingEntitySelectbox'
import { SubjectSelectboxField } from 'view/shared/entity/SubjectTradingEntitySelectbox'
import { ClonedComponentByShowModal } from 'view/shared/modal'

export interface PrimeBrokerDefaultsAddDialogProps extends ClonedComponentByShowModal {
  workflowInfo?: WorkflowInfo
}

interface WorkflowInfo {
  workflowId: string | null
  metaId: string | null
}

const workflowReferenceDataSelector = createWorkflowReferenceDataEntitySelector(
  EntityType.TRADING_ENTITY_META,
  (_state: GlobalState, workflowInfo: WorkflowInfo) => workflowInfo.workflowId ?? '',
  (_state: GlobalState, workflowInfo: WorkflowInfo) => workflowInfo.metaId ?? ''
)

export const PrimeBrokerDefaultsAddDialog = (props: PrimeBrokerDefaultsAddDialogProps) => {
  const [workflowInfo, setWorkflowInfo] = useState<WorkflowInfo>({
    workflowId: props.workflowInfo?.workflowId ?? null,
    metaId: props.workflowInfo?.metaId ?? null
  })
  const workflowReference = useParametricSelector(workflowReferenceDataSelector, workflowInfo)

  const validationIssues = workflowReference?.validationIssues ?? []
  const getMetas = useSelector(tradingEntityMetasGetterSelector)
  const dispatch = useDispatch()
  const [ownerTradingEntityId, setOwnerTradingEntityId] = useState<string | null>(null)

  useEffect(
    function loadEditModeOnMount() {
      if (workflowReference?.data && !ownerTradingEntityId) {
        setOwnerTradingEntityId(workflowReference.data.ownerTradingEntityId)
      }
    },
    [ownerTradingEntityId, props.workflowInfo, workflowReference?.data]
  )

  function cancelWorkflow(id: string) {
    dispatch(WorkflowActions.createCancelWorkflow(id))
    setWorkflowInfo({ workflowId: null, metaId: null })
  }

  function onSubmit() {
    if (!workflowInfo.workflowId || validationIssues.length) return

    dispatch(WorkflowActions.createSubmitWorkflow(workflowInfo.workflowId))
    props?.onClose?.()
  }

  function onSelectMyEntity(ownerId: string) {
    workflowInfo.workflowId && cancelWorkflow(workflowInfo.workflowId)
    setOwnerTradingEntityId(ownerId)
  }

  function onSelectCounterparty(counterpartyId: string, ownerId: string) {
    const metas = getMetas(ownerId)
    const metaFound = metas.find(m => m.subjectTradingEntityId === counterpartyId && ownerTradingEntityId === ownerTradingEntityId)

    if (!metaFound) return

    const isNewMeta = metaFound.id.startsWith(NEW_ITEM_PREFIX)
    workflowInfo.workflowId && cancelWorkflow(workflowInfo.workflowId)

    if (isNewMeta && ownerTradingEntityId) {
      dispatch(
        WorkflowActions.createNewWorkflow(
          EntityTypeEnum.TRADING_ENTITY_META,
          (workflowId: string, newlyAddedEntityIdAndTypes: EntityTypeIdRecord) => {
            const id = newlyAddedEntityIdAndTypes[EntityTypeEnum.TRADING_ENTITY_META]
            setWorkflowInfo({ workflowId, metaId: id })
            dispatch(
              WorkflowActions.createEntityUpdateReferenceData<TradingEntityMeta>({
                entityId: id,
                entityType: EntityTypeEnum.TRADING_ENTITY_META,
                workflowId,
                updatedEntity: {
                  id,
                  ownerTradingEntityId,
                  subjectTradingEntityId: counterpartyId,
                  primaryCoverUserId: null,
                  primeBrokerId: null,
                  secondaryCoverUserId: null,
                  aliasTypeAndValues: []
                }
              })
            )
          }
        )
      )
    } else {
      dispatch(
        WorkflowActions.createEditWorkflow(EntityTypeEnum.TRADING_ENTITY_META, metaFound.id, (workflowId: string) => {
          setWorkflowInfo({ workflowId, metaId: metaFound.id })
        })
      )
    }
  }

  function onSelectPrimeBroker(primeBrokerId: string) {
    if (!workflowInfo.workflowId || !workflowReference?.data) return

    dispatch(
      WorkflowActions.createEntityUpdateReferenceData<TradingEntityMeta>({
        entityId: workflowReference.data.id,
        entityType: EntityTypeEnum.TRADING_ENTITY_META,
        workflowId: workflowInfo.workflowId,
        updatedEntity: {
          ...workflowReference.data,
          primeBrokerId
        }
      })
    )
  }

  return (
    <DialogWindow
      title={Translation.TC_ADD_PRIME_BROKER}
      onConfirm={onSubmit}
      confirmButtonEnabled={!!workflowInfo.workflowId && validationIssues.length === 0 && !!workflowReference?.data?.primeBrokerId}
      confirmLabel={Translation.TC_BUTTON_OK}
      onClose={props?.onClose}>
      <Groupbox>
        <DialogRow>
          <DialogField>
            <OwnerSelectboxField
              dataTestId="PrimerBrokerDefaultsAddDialog-MyEntity-Dropdown"
              fieldName="ownerTradingEntityId"
              label={Locale.translate(Translation.SC_ADD_PRIME_BROKER_MY_ENTITY)}
              value={ownerTradingEntityId}
              validationIssues={validationIssues}
              onUpdate={id => onSelectMyEntity(id)}
            />
          </DialogField>
          <DialogField>
            <SubjectSelectboxField
              ownerTradingEntityId={ownerTradingEntityId}
              fieldName="subjectTradingEntityId"
              label={Locale.translate(Translation.SC_ADD_PRIME_BROKER_COUNTERPARTY)}
              validationIssues={validationIssues}
              disabled={!ownerTradingEntityId}
              readOnly={!ownerTradingEntityId}
              value={workflowReference?.data?.subjectTradingEntityId}
              onUpdate={id => {
                ownerTradingEntityId && onSelectCounterparty(id, ownerTradingEntityId)
              }}
            />
          </DialogField>
        </DialogRow>
        <DialogRow>
          <DialogField>
            <PrimeBrokerSelectboxField
              canClear={false}
              fieldName="primeBroker"
              label={Locale.translate(Translation.SC_ADD_PRIME_BROKER_PRIME_BROKER)}
              validationIssues={validationIssues}
              disabled={!workflowInfo.workflowId}
              readOnly={false}
              value={workflowReference?.data?.primeBrokerId}
              onUpdate={id => {
                onSelectPrimeBroker(id)
              }}
            />
          </DialogField>
        </DialogRow>
      </Groupbox>
    </DialogWindow>
  )
}
